import React, { Fragment, useState } from "react";

import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import Dropdown, { DropdownItem } from "@common/components/Dropdown/Dropdown";
import { GridTable } from "@common/components/GridTable/GridTable";
import { HelperText } from "@common/components/HelperText/HelperText";
import { useModal } from "@common/components/Modal/Modal";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { App, Workspace } from "@common/models";
import { AddApiKeyResult, ApiKey, ApiKeyType } from "@common/models/ApiKey.model";
import { isAdmin } from "@common/models/Member.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { docsLinks, openDocsInNewTab } from "@common/util/docs";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import { showIntercomArticle } from "@common/util/intercom";
import { t } from "@common/util/translator";
import { useShakingAnimation } from "@main/context/ShakingAnimation/ShakingAnimationContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate } from "react-router-dom";

import { ApiKeyRow } from "./ApiKeyRow";
import DeleteApiKeyModalBody from "../ModalBody/DeleteApiKeyModalBody";
import RestApiPlanLimitModalBody from "../ModalBody/RestApiPlanLimitModalBody";
import RestApiTrialModalBody from "../ModalBody/RestApiTrialModalBody";

interface ApiKeysCardProps {
  app: App;
  loading: boolean;
  addApiKey: (type: ApiKeyType, startTrial?: boolean) => Promise<AddApiKeyResult>;
  deleteApiKey: (id: string) => Promise<void>;
  selectedWorkspace?: Workspace;
}

export default function ApiKeysCard({
  loading,
  app,
  addApiKey,
  deleteApiKey,
  selectedWorkspace,
}: ApiKeysCardProps): JSX.Element {
  const { api_keys } = app;
  const { triggerShake } = useShakingAnimation();
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { Modal: DeleteApiKeyModal, toggle: toggleDeleteModal, modalProps } = useModal({ size: "narrow" });
  const {
    Modal: RestApiTrialModal,
    toggle: toggleRestApiTrialModal,
    modalProps: restApiTrialModalProps,
  } = useModal({ size: "narrow" });
  const {
    Modal: RestApiPlanLimitModal,
    toggle: toggleRestApiPlanLimitModal,
    modalProps: restApiPlanLimitModalProps,
  } = useModal({ size: "narrow" });
  const navigate = useNavigate();

  const handleApiKeyDelete = async () => {
    if (app.is_sample) {
      triggerShake("AddAppButton");
      return;
    }
    if (!selectedApiKey) return;
    await deleteApiKey(selectedApiKey.id);
    toggleDeleteModal();
  };

  const handleApiKeyCreate = async (type: ApiKeyType) => {
    if (app.is_sample) {
      triggerShake("AddAppButton");
      return;
    }
    setIsDropdownOpen(false);
    if (type === ApiKeyType.API && selectedWorkspace?.rest_api_trial_available) {
      toggleRestApiTrialModal();
      return;
    }
    if ((await addApiKey(type)) === "plan_limited") toggleRestApiPlanLimitModal();
  };

  const handleRestApiTrialStart = async () => {
    const result = await addApiKey(ApiKeyType.API, true);
    toggleRestApiTrialModal();
    if (result === "plan_limited") toggleRestApiPlanLimitModal();
  };

  const handleSeePlans = () => {
    toggleRestApiPlanLimitModal();
    if (selectedWorkspace)
      navigate(generatePath(RoutePaths.ADMINISTRATION_BILLING, { workspaceSlug: selectedWorkspace.slug }));
  };

  const tooltipDisabled = app.active && isAdmin(selectedWorkspace?.role) && !app.is_sample;
  const tooltipText = app.is_sample
    ? "Sample app can't be modified"
    : !isAdmin(selectedWorkspace?.role)
    ? "Only admins can add API keys"
    : "API keys can be added on active apps only";

  return (
    <Fragment>
      <AdministrationCard
        title={t("administration.general.card.apiKeys.heading")}
        subtitle={t("administration.general.card.apiKeys.subtitle")}
        topLeftAccessory={
          <Dropdown
            style={{ height: "auto" }}
            isOpened={isDropdownOpen}
            setIsOpened={setIsDropdownOpen}
            buttonElement={
              <Button
                size="small"
                disabled={loading || !app.active || !isAdmin(selectedWorkspace?.role)}
                loadingText={loading && "Creating..."}
                tooltip={{
                  text: tooltipText,
                  disabled: tooltipDisabled,
                  position: "top",
                }}
              >
                {"Create key"}
              </Button>
            }
            disabled={loading || !app.active || !isAdmin(selectedWorkspace?.role)}
            position="left"
          >
            <DropdownItem onClick={() => handleApiKeyCreate(ApiKeyType.SDK)}>
              <Flex $column>
                <Paragraph
                  style={{ color: vars.colors.grey30, whiteSpace: "nowrap" }}
                  p2
                >
                  {"App API key"}
                </Paragraph>
                <Paragraph
                  style={{ color: vars.colors.grey30, whiteSpace: "nowrap" }}
                  p3
                >
                  {"Initialize the Shake SDK"}
                </Paragraph>
              </Flex>
            </DropdownItem>
            <DropdownItem onClick={() => handleApiKeyCreate(ApiKeyType.API)}>
              <Flex $column>
                <Paragraph
                  style={{ color: vars.colors.grey30, whiteSpace: "nowrap" }}
                  p2
                >
                  {"REST API key"}
                </Paragraph>
                <Paragraph
                  style={{ color: vars.colors.grey30, whiteSpace: "nowrap" }}
                  p3
                >
                  {"Access Shake data programmatically"}
                </Paragraph>
              </Flex>
            </DropdownItem>
          </Dropdown>
        }
      >
        {api_keys && (
          <GridTable gridTemplateColumns="repeat(4, auto)">
            {api_keys.map((api_key) => {
              return (
                <ApiKeyRow
                  key={api_key.id}
                  setSelectedApiKey={setSelectedApiKey}
                  toggleDelete={toggleDeleteModal}
                  apiKey={api_key}
                  isAdmin={isAdmin(selectedWorkspace?.role)}
                />
              );
            })}
          </GridTable>
        )}
        <HelperText>
          {"Two useful links: "}
          <br />
          &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
          <a onClick={() => openDocsInNewTab(docsLinks.generic.restApi)}>{"REST API docs"}</a>
          <br />
          &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp; {"See example of using App API keys and REST API keys "}
          <a onClick={() => showIntercomArticle(helpCenterArticles.deprecatedClientKeys)}>{"in this article"}</a>
          {"."}
        </HelperText>
      </AdministrationCard>

      <DeleteApiKeyModalBody
        deleteApiKey={handleApiKeyDelete}
        apiKey={selectedApiKey}
        loading={loading}
        Modal={DeleteApiKeyModal}
        internalProps={modalProps}
      />

      <RestApiTrialModalBody
        startTrial={handleRestApiTrialStart}
        loading={loading}
        Modal={RestApiTrialModal}
        internalProps={restApiTrialModalProps}
      />

      <RestApiPlanLimitModalBody
        seePlans={handleSeePlans}
        Modal={RestApiPlanLimitModal}
        internalProps={restApiPlanLimitModalProps}
      />
    </Fragment>
  );
}
